import { Pokemon, Tier } from "../types/pokemon";

export interface SmogonSet {
  name: string;
  item: string;
  ability: string;
  nature: string;
  evs: { hp?: number; atk?: number; def?: number; spa?: number; spd?: number; spe?: number };
  moves: [string, string, string, string];
  tier: "ou" | "uu" | "ag";
}

export interface PokemonSets {
  [pokemonName: string]: SmogonSet[];
}

function capitalizeShowdownName(name: string): string {
  return name.split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join("-");
}

export function getBuildForPokemon(pokemon: Pokemon, tier: Tier): SmogonSet {
  const lookupName = capitalizeShowdownName(pokemon.name);
  const sets = SMOGON_SETS[lookupName];
  if (sets) {
    if (tier === "all") return sets[0]; // "all" = first available set
    const tierMatch = sets.find(s => s.tier === tier);
    if (tierMatch) return tierMatch;
    return sets[0];
  }
  return generateFallbackBuild(pokemon);
}

// Type-based STAB moves (reliable, widely distributed)
const TYPE_STAB_MOVES: Record<string, string[]> = {
  normal: ["Return", "Body Slam", "Hyper Voice", "Facade"],
  fire: ["Flamethrower", "Fire Blast", "Flare Blitz", "Heat Wave"],
  water: ["Surf", "Scald", "Hydro Pump", "Liquidation"],
  electric: ["Thunderbolt", "Thunder", "Volt Switch", "Wild Charge"],
  grass: ["Energy Ball", "Giga Drain", "Leaf Blade", "Leaf Storm"],
  ice: ["Ice Beam", "Blizzard", "Ice Punch", "Freeze-Dry"],
  fighting: ["Close Combat", "Aura Sphere", "Drain Punch", "Focus Blast"],
  poison: ["Sludge Bomb", "Sludge Wave", "Poison Jab", "Gunk Shot"],
  ground: ["Earthquake", "Earth Power", "Stomping Tantrum", "High Horsepower"],
  flying: ["Air Slash", "Hurricane", "Brave Bird", "Aerial Ace"],
  psychic: ["Psychic", "Psyshock", "Zen Headbutt", "Expanding Force"],
  bug: ["Bug Buzz", "U-turn", "X-Scissor", "Megahorn"],
  rock: ["Stone Edge", "Rock Slide", "Power Gem", "Head Smash"],
  ghost: ["Shadow Ball", "Hex", "Shadow Claw", "Phantom Force"],
  dragon: ["Dragon Pulse", "Draco Meteor", "Dragon Claw", "Outrage"],
  dark: ["Dark Pulse", "Knock Off", "Crunch", "Sucker Punch"],
  steel: ["Flash Cannon", "Iron Head", "Steel Beam", "Heavy Slam"],
  fairy: ["Moonblast", "Dazzling Gleam", "Play Rough", "Draining Kiss"],
};

// Good coverage moves by type
const COVERAGE_MOVES: Record<string, string[]> = {
  fire: ["Solar Beam", "Focus Blast", "Thunderbolt"],
  water: ["Ice Beam", "Energy Ball", "Psychic"],
  grass: ["Sludge Bomb", "Earth Power", "Weather Ball"],
  electric: ["Ice Beam", "Focus Blast", "Volt Switch"],
  psychic: ["Shadow Ball", "Focus Blast", "Energy Ball"],
  ghost: ["Dark Pulse", "Thunderbolt", "Energy Ball"],
  dragon: ["Fire Blast", "Earthquake", "Ice Beam"],
  fighting: ["Ice Punch", "Thunder Punch", "Knock Off"],
  dark: ["Fire Blast", "Focus Blast", "Psychic"],
  steel: ["Body Press", "Thunderbolt", "Flamethrower"],
  fairy: ["Thunderbolt", "Flamethrower", "Psychic"],
  flying: ["Earthquake", "Close Combat", "U-turn"],
  ground: ["Stone Edge", "Ice Punch", "Knock Off"],
  rock: ["Earthquake", "Close Combat", "Ice Punch"],
  poison: ["Earthquake", "Shadow Ball", "Psychic"],
  ice: ["Thunderbolt", "Earth Power", "Shadow Ball"],
  bug: ["Close Combat", "Knock Off", "Aerial Ace"],
  normal: ["Earthquake", "Shadow Ball", "Ice Beam"],
};

// Utility moves
const UTILITY_MOVES = [
  "Protect", "Stealth Rock", "Toxic", "Will-O-Wisp", "Thunder Wave",
  "Roost", "Recover", "Synthesis", "Wish", "Defog",
];

// Items by role
const PHYSICAL_ITEMS = ["Choice Band", "Life Orb", "Assault Vest", "Lum Berry", "Heavy-Duty Boots"];
const SPECIAL_ITEMS = ["Choice Specs", "Life Orb", "Assault Vest", "Focus Sash", "Heavy-Duty Boots"];
const BULKY_ITEMS = ["Leftovers", "Rocky Helmet", "Heavy-Duty Boots", "Sitrus Berry", "Eviolite"];

// Natures by stat focus
function pickNature(isPhysical: boolean, isBulky: boolean): string {
  if (isBulky) return isPhysical ? "Impish" : "Calm";
  return isPhysical ? "Jolly" : "Timid";
}

function generateFallbackBuild(pokemon: Pokemon): SmogonSet {
  const statsMap: Record<string, number> = {};
  for (const s of pokemon.stats) statsMap[s.name] = s.value;

  const atk = statsMap["attack"] || 0;
  const spa = statsMap["special-attack"] || 0;
  const def = statsMap["defense"] || 0;
  const spd = statsMap["special-defense"] || 0;
  const spe = statsMap["speed"] || 0;
  const hp = statsMap["hp"] || 0;

  const isPhysical = atk >= spa;
  const isBulky = (def + spd + hp) > (atk + spa + spe) * 1.2;

  // Pick type-based moves
  const type1 = pokemon.types[0] || "normal";
  const type2 = pokemon.types[1];

  const stab1 = TYPE_STAB_MOVES[type1] || TYPE_STAB_MOVES.normal;
  const stab1Move = isPhysical ? stab1[2] || stab1[0] : stab1[0]; // physical variants tend to be index 2-3

  let stab2Move = "";
  if (type2) {
    const stab2 = TYPE_STAB_MOVES[type2] || TYPE_STAB_MOVES.normal;
    stab2Move = isPhysical ? stab2[2] || stab2[0] : stab2[0];
  }

  // Coverage move
  const coverage = COVERAGE_MOVES[type1] || COVERAGE_MOVES.normal;
  const coverageMove = coverage[Math.floor(Math.random() * coverage.length)];

  // 4th move: utility if bulky, coverage if offensive
  let move4: string;
  if (isBulky) {
    move4 = UTILITY_MOVES[Math.floor(Math.random() * 5)]; // first 5 are more defensive
  } else {
    const alt = COVERAGE_MOVES[type2 || type1] || COVERAGE_MOVES.normal;
    move4 = alt.find(m => m !== coverageMove) || "Protect";
  }

  const moves: [string, string, string, string] = [
    stab1Move,
    stab2Move || coverageMove,
    stab2Move ? coverageMove : move4,
    stab2Move ? move4 : (isBulky ? "Protect" : "Substitute"),
  ];

  // EVs
  const evs: SmogonSet["evs"] = {};
  if (isBulky) {
    evs.hp = 252;
    evs[isPhysical ? "def" : "spd"] = 252;
    evs[isPhysical ? "atk" : "spa"] = 4;
  } else {
    evs[isPhysical ? "atk" : "spa"] = 252;
    evs.spe = 252;
    evs.hp = 4;
  }

  // Item
  const items = isBulky ? BULKY_ITEMS : (isPhysical ? PHYSICAL_ITEMS : SPECIAL_ITEMS);
  const item = items[Math.floor(Math.random() * items.length)];

  return {
    name: "Auto Build",
    item,
    ability: "", // Showdown will auto-assign the first legal ability
    nature: pickNature(isPhysical, isBulky),
    evs,
    moves,
    tier: "ou",
  };
}

// Top OU/UU Pokemon with competitive sets (Gen 9 Smogon)
export const SMOGON_SETS: PokemonSets = {
  // ─── GEN 9 OU ────────────────────────────────────────────────────────────────

  Dragapult: [
    {
      name: "Choice Specs",
      item: "Choice Specs",
      ability: "Clear Body",
      nature: "Timid",
      evs: { spa: 252, spd: 4, spe: 252 },
      moves: ["Draco Meteor", "Shadow Ball", "Flamethrower", "U-turn"],
      tier: "ou",
    },
    {
      name: "Dragon Dance",
      item: "Loaded Dice",
      ability: "Clear Body",
      nature: "Jolly",
      evs: { atk: 252, def: 4, spe: 252 },
      moves: ["Dragon Dance", "Dragon Darts", "Phantom Force", "Fire Spin"],
      tier: "ou",
    },
  ],

  "Great-Tusk": [
    {
      name: "Offensive Stealth Rock",
      item: "Booster Energy",
      ability: "Protosynthesis",
      nature: "Jolly",
      evs: { hp: 4, atk: 252, spe: 252 },
      moves: ["Stealth Rock", "Headlong Rush", "Ice Spinner", "Knock Off"],
      tier: "ou",
    },
    {
      name: "Bulky Rapid Spin",
      item: "Heavy-Duty Boots",
      ability: "Protosynthesis",
      nature: "Impish",
      evs: { hp: 252, atk: 68, def: 188 },
      moves: ["Rapid Spin", "Headlong Rush", "Ice Spinner", "Stealth Rock"],
      tier: "ou",
    },
  ],

  Gholdengo: [
    {
      name: "Nasty Plot",
      item: "Air Balloon",
      ability: "Good as Gold",
      nature: "Timid",
      evs: { spa: 252, spd: 4, spe: 252 },
      moves: ["Nasty Plot", "Make It Rain", "Shadow Ball", "Focus Blast"],
      tier: "ou",
    },
    {
      name: "Choice Specs",
      item: "Choice Specs",
      ability: "Good as Gold",
      nature: "Timid",
      evs: { spa: 252, spd: 4, spe: 252 },
      moves: ["Make It Rain", "Shadow Ball", "Focus Blast", "Trick"],
      tier: "ou",
    },
  ],

  Kingambit: [
    {
      name: "Supreme Overlord Sweeper",
      item: "Black Glasses",
      ability: "Supreme Overlord",
      nature: "Adamant",
      evs: { hp: 4, atk: 252, spe: 252 },
      moves: ["Swords Dance", "Kowtow Cleave", "Iron Head", "Sucker Punch"],
      tier: "ou",
    },
  ],

  "Iron-Valiant": [
    {
      name: "Calm Mind",
      item: "Booster Energy",
      ability: "Quark Drive",
      nature: "Timid",
      evs: { spa: 252, spd: 4, spe: 252 },
      moves: ["Calm Mind", "Moonblast", "Psyshock", "Focus Blast"],
      tier: "ou",
    },
    {
      name: "Swords Dance",
      item: "Booster Energy",
      ability: "Quark Drive",
      nature: "Jolly",
      evs: { atk: 252, def: 4, spe: 252 },
      moves: ["Swords Dance", "Close Combat", "Spirit Break", "Shadow Sneak"],
      tier: "ou",
    },
  ],

  Darkrai: [
    {
      name: "Nasty Plot",
      item: "Focus Sash",
      ability: "Bad Dreams",
      nature: "Timid",
      evs: { spa: 252, spd: 4, spe: 252 },
      moves: ["Nasty Plot", "Dark Pulse", "Ice Beam", "Focus Blast"],
      tier: "ou",
    },
  ],

  Skeledirge: [
    {
      name: "Offensive Unaware",
      item: "Heavy-Duty Boots",
      ability: "Unaware",
      nature: "Modest",
      evs: { hp: 252, spa: 252, spd: 4 },
      moves: ["Torch Song", "Shadow Ball", "Slack Off", "Hex"],
      tier: "ou",
    },
  ],

  Garchomp: [
    {
      name: "Stealth Rock",
      item: "Rocky Helmet",
      ability: "Rough Skin",
      nature: "Impish",
      evs: { hp: 252, def: 168, spe: 88 },
      moves: ["Stealth Rock", "Earthquake", "Dragon Tail", "Toxic"],
      tier: "ou",
    },
    {
      name: "Swords Dance",
      item: "Choice Scarf",
      ability: "Rough Skin",
      nature: "Jolly",
      evs: { atk: 252, def: 4, spe: 252 },
      moves: ["Earthquake", "Dragon Claw", "Poison Jab", "Fire Fang"],
      tier: "ou",
    },
  ],

  Heatran: [
    {
      name: "Specially Defensive",
      item: "Leftovers",
      ability: "Flash Fire",
      nature: "Calm",
      evs: { hp: 252, spd: 252, spe: 4 },
      moves: ["Magma Storm", "Earth Power", "Toxic", "Protect"],
      tier: "ou",
    },
    {
      name: "Offensive",
      item: "Choice Scarf",
      ability: "Flash Fire",
      nature: "Timid",
      evs: { spa: 252, spd: 4, spe: 252 },
      moves: ["Magma Storm", "Earth Power", "Flash Cannon", "Dark Pulse"],
      tier: "ou",
    },
  ],

  "Landorus-Therian": [
    {
      name: "Defensive Pivot",
      item: "Rocky Helmet",
      ability: "Intimidate",
      nature: "Impish",
      evs: { hp: 252, def: 240, spe: 16 },
      moves: ["Stealth Rock", "Earthquake", "U-turn", "Knock Off"],
      tier: "ou",
    },
    {
      name: "Choice Scarf",
      item: "Choice Scarf",
      ability: "Intimidate",
      nature: "Jolly",
      evs: { atk: 252, def: 4, spe: 252 },
      moves: ["Earthquake", "U-turn", "Stone Edge", "Knock Off"],
      tier: "ou",
    },
  ],

  Gliscor: [
    {
      name: "Toxic Stall",
      item: "Toxic Orb",
      ability: "Poison Heal",
      nature: "Impish",
      evs: { hp: 244, def: 236, spe: 28 },
      moves: ["Toxic", "Protect", "Earthquake", "Knock Off"],
      tier: "ou",
    },
    {
      name: "Swords Dance",
      item: "Toxic Orb",
      ability: "Poison Heal",
      nature: "Jolly",
      evs: { hp: 4, atk: 252, spe: 252 },
      moves: ["Swords Dance", "Earthquake", "Ice Fang", "Knock Off"],
      tier: "ou",
    },
  ],

  Toxapex: [
    {
      name: "Physically Defensive",
      item: "Black Sludge",
      ability: "Regenerator",
      nature: "Bold",
      evs: { hp: 252, def: 252, spd: 4 },
      moves: ["Scald", "Toxic Spikes", "Recover", "Haze"],
      tier: "ou",
    },
  ],

  Corviknight: [
    {
      name: "Physically Defensive",
      item: "Leftovers",
      ability: "Pressure",
      nature: "Impish",
      evs: { hp: 252, def: 252, spd: 4 },
      moves: ["Brave Bird", "Body Press", "Roost", "Defog"],
      tier: "ou",
    },
    {
      name: "Bulk Up",
      item: "Leftovers",
      ability: "Unnerve",
      nature: "Careful",
      evs: { hp: 252, spd: 252, def: 4 },
      moves: ["Bulk Up", "Body Press", "Roost", "Brave Bird"],
      tier: "ou",
    },
  ],

  Ferrothorn: [
    {
      name: "Spikes Setter",
      item: "Leftovers",
      ability: "Iron Barbs",
      nature: "Relaxed",
      evs: { hp: 252, def: 252, spd: 4 },
      moves: ["Spikes", "Stealth Rock", "Leech Seed", "Power Whip"],
      tier: "ou",
    },
  ],

  Clefable: [
    {
      name: "Calm Mind",
      item: "Leftovers",
      ability: "Magic Guard",
      nature: "Calm",
      evs: { hp: 252, def: 4, spd: 252 },
      moves: ["Calm Mind", "Moonblast", "Flamethrower", "Soft-Boiled"],
      tier: "ou",
    },
    {
      name: "Stealth Rock Support",
      item: "Leftovers",
      ability: "Magic Guard",
      nature: "Bold",
      evs: { hp: 252, def: 252, spd: 4 },
      moves: ["Stealth Rock", "Moonblast", "Soft-Boiled", "Encore"],
      tier: "ou",
    },
  ],

  "Slowking-Galar": [
    {
      name: "Specially Defensive",
      item: "Heavy-Duty Boots",
      ability: "Regenerator",
      nature: "Sassy",
      evs: { hp: 252, def: 4, spd: 252 },
      moves: ["Sludge Bomb", "Future Sight", "Slack Off", "Chilly Reception"],
      tier: "ou",
    },
  ],

  Dragonite: [
    {
      name: "Multiscale Sweeper",
      item: "Lum Berry",
      ability: "Multiscale",
      nature: "Adamant",
      evs: { atk: 252, def: 4, spe: 252 },
      moves: ["Dragon Dance", "Extreme Speed", "Earthquake", "Fire Punch"],
      tier: "ou",
    },
    {
      name: "Choice Band",
      item: "Choice Band",
      ability: "Multiscale",
      nature: "Adamant",
      evs: { atk: 252, spd: 4, spe: 252 },
      moves: ["Extreme Speed", "Outrage", "Earthquake", "Fire Punch"],
      tier: "ou",
    },
  ],

  Volcarona: [
    {
      name: "Quiver Dance",
      item: "Heavy-Duty Boots",
      ability: "Flame Body",
      nature: "Timid",
      evs: { spa: 252, spd: 4, spe: 252 },
      moves: ["Quiver Dance", "Fire Blast", "Bug Buzz", "Giga Drain"],
      tier: "ou",
    },
  ],

  Weavile: [
    {
      name: "Choice Band",
      item: "Choice Band",
      ability: "Pressure",
      nature: "Jolly",
      evs: { atk: 252, def: 4, spe: 252 },
      moves: ["Triple Axel", "Knock Off", "Ice Shard", "Low Kick"],
      tier: "ou",
    },
    {
      name: "Swords Dance",
      item: "Life Orb",
      ability: "Pressure",
      nature: "Jolly",
      evs: { atk: 252, def: 4, spe: 252 },
      moves: ["Swords Dance", "Triple Axel", "Knock Off", "Ice Shard"],
      tier: "ou",
    },
  ],

  Zamazenta: [
    {
      name: "Specially Defensive",
      item: "Leftovers",
      ability: "Dauntless Shield",
      nature: "Careful",
      evs: { hp: 252, atk: 4, spd: 252 },
      moves: ["Body Press", "Crunch", "Howl", "Rest"],
      tier: "ou",
    },
  ],

  "Iron-Moth": [
    {
      name: "Quiver Dance",
      item: "Booster Energy",
      ability: "Quark Drive",
      nature: "Timid",
      evs: { spa: 252, spd: 4, spe: 252 },
      moves: ["Quiver Dance", "Fiery Dance", "Sludge Wave", "Energy Ball"],
      tier: "ou",
    },
  ],

  "Roaring-Moon": [
    {
      name: "Dragon Dance",
      item: "Booster Energy",
      ability: "Protosynthesis",
      nature: "Jolly",
      evs: { atk: 252, def: 4, spe: 252 },
      moves: ["Dragon Dance", "Acrobatics", "Earthquake", "Crunch"],
      tier: "ou",
    },
  ],

  "Iron-Hands": [
    {
      name: "Belly Drum",
      item: "Sitrus Berry",
      ability: "Quark Drive",
      nature: "Adamant",
      evs: { hp: 4, atk: 252, spe: 252 },
      moves: ["Belly Drum", "Drain Punch", "Wild Charge", "Ice Punch"],
      tier: "ou",
    },
  ],

  Palafin: [
    {
      name: "Jet Punch Sweeper",
      item: "Choice Band",
      ability: "Zero to Hero",
      nature: "Adamant",
      evs: { atk: 252, def: 4, spe: 252 },
      moves: ["Jet Punch", "Wave Crash", "Close Combat", "Ice Punch"],
      tier: "ou",
    },
  ],

  Meowscarada: [
    {
      name: "Choice Band",
      item: "Choice Band",
      ability: "Overgrow",
      nature: "Jolly",
      evs: { atk: 252, def: 4, spe: 252 },
      moves: ["Flower Trick", "U-turn", "Play Rough", "Knock Off"],
      tier: "ou",
    },
  ],

  "Ting-Lu": [
    {
      name: "Physically Defensive",
      item: "Leftovers",
      ability: "Vessel of Ruin",
      nature: "Impish",
      evs: { hp: 252, def: 252, spe: 4 },
      moves: ["Stealth Rock", "Ruination", "Whirlwind", "Earthquake"],
      tier: "ou",
    },
  ],

  "Chi-Yu": [
    {
      name: "Choice Scarf",
      item: "Choice Scarf",
      ability: "Beads of Ruin",
      nature: "Timid",
      evs: { spa: 252, spd: 4, spe: 252 },
      moves: ["Overheat", "Dark Pulse", "Flamethrower", "Psychic"],
      tier: "ou",
    },
  ],

  "Flutter-Mane": [
    {
      name: "Choice Specs",
      item: "Choice Specs",
      ability: "Protosynthesis",
      nature: "Timid",
      evs: { spa: 252, spd: 4, spe: 252 },
      moves: ["Moonblast", "Shadow Ball", "Psyshock", "Thunderbolt"],
      tier: "ou",
    },
  ],

  Ogerpon: [
    {
      name: "Swords Dance",
      item: "Wellspring Mask",
      ability: "Water Absorb",
      nature: "Jolly",
      evs: { atk: 252, def: 4, spe: 252 },
      moves: ["Swords Dance", "Ivy Cudgel", "Horn Leech", "Follow Me"],
      tier: "ou",
    },
  ],

  "Urshifu-Rapid-Strike": [
    {
      name: "Choice Band",
      item: "Choice Band",
      ability: "Unseen Fist",
      nature: "Jolly",
      evs: { atk: 252, def: 4, spe: 252 },
      moves: ["Surging Strikes", "Close Combat", "Aqua Jet", "U-turn"],
      tier: "ou",
    },
  ],

  "Iron-Boulder": [
    {
      name: "Swords Dance",
      item: "Booster Energy",
      ability: "Quark Drive",
      nature: "Jolly",
      evs: { atk: 252, def: 4, spe: 252 },
      moves: ["Swords Dance", "Stone Axe", "Close Combat", "Zen Headbutt"],
      tier: "ou",
    },
  ],

  // ─── GEN 9 UU ────────────────────────────────────────────────────────────────

  Scizor: [
    {
      name: "Swords Dance",
      item: "Life Orb",
      ability: "Technician",
      nature: "Adamant",
      evs: { hp: 4, atk: 252, spe: 252 },
      moves: ["Swords Dance", "Bullet Punch", "Bug Bite", "Knock Off"],
      tier: "uu",
    },
    {
      name: "Bulky Pivot",
      item: "Heavy-Duty Boots",
      ability: "Technician",
      nature: "Adamant",
      evs: { hp: 248, atk: 252, spd: 8 },
      moves: ["Bullet Punch", "U-turn", "Roost", "Knock Off"],
      tier: "uu",
    },
  ],

  Hydreigon: [
    {
      name: "Choice Specs",
      item: "Choice Specs",
      ability: "Levitate",
      nature: "Timid",
      evs: { spa: 252, spd: 4, spe: 252 },
      moves: ["Draco Meteor", "Dark Pulse", "Flamethrower", "U-turn"],
      tier: "uu",
    },
    {
      name: "Nasty Plot",
      item: "Life Orb",
      ability: "Levitate",
      nature: "Timid",
      evs: { spa: 252, spd: 4, spe: 252 },
      moves: ["Nasty Plot", "Draco Meteor", "Dark Pulse", "Flash Cannon"],
      tier: "uu",
    },
  ],

  Primarina: [
    {
      name: "Choice Specs",
      item: "Choice Specs",
      ability: "Torrent",
      nature: "Modest",
      evs: { hp: 4, spa: 252, spe: 252 },
      moves: ["Moonblast", "Hydro Pump", "Sparkling Aria", "Energy Ball"],
      tier: "uu",
    },
    {
      name: "Calm Mind",
      item: "Leftovers",
      ability: "Liquid Voice",
      nature: "Timid",
      evs: { hp: 252, spd: 252, def: 4 },
      moves: ["Calm Mind", "Moonblast", "Sparkling Aria", "Encore"],
      tier: "uu",
    },
  ],

  Salamence: [
    {
      name: "Choice Scarf",
      item: "Choice Scarf",
      ability: "Intimidate",
      nature: "Jolly",
      evs: { atk: 252, def: 4, spe: 252 },
      moves: ["Outrage", "Earthquake", "Dragon Claw", "Fire Fang"],
      tier: "uu",
    },
    {
      name: "Dragon Dance",
      item: "Lum Berry",
      ability: "Intimidate",
      nature: "Adamant",
      evs: { atk: 252, def: 4, spe: 252 },
      moves: ["Dragon Dance", "Return", "Outrage", "Earthquake"],
      tier: "uu",
    },
  ],

  Moltres: [
    {
      name: "Specially Defensive",
      item: "Heavy-Duty Boots",
      ability: "Flame Body",
      nature: "Calm",
      evs: { hp: 248, def: 8, spd: 252 },
      moves: ["Flamethrower", "Hurricane", "Roost", "U-turn"],
      tier: "uu",
    },
  ],

  Krookodile: [
    {
      name: "Choice Scarf",
      item: "Choice Scarf",
      ability: "Intimidate",
      nature: "Jolly",
      evs: { atk: 252, def: 4, spe: 252 },
      moves: ["Earthquake", "Knock Off", "Outrage", "Stone Edge"],
      tier: "uu",
    },
    {
      name: "Stealth Rock",
      item: "Life Orb",
      ability: "Intimidate",
      nature: "Jolly",
      evs: { atk: 252, def: 4, spe: 252 },
      moves: ["Stealth Rock", "Earthquake", "Knock Off", "Stone Edge"],
      tier: "uu",
    },
  ],

  Lucario: [
    {
      name: "Swords Dance",
      item: "Life Orb",
      ability: "Inner Focus",
      nature: "Adamant",
      evs: { atk: 252, spd: 4, spe: 252 },
      moves: ["Swords Dance", "Close Combat", "Bullet Punch", "Crunch"],
      tier: "uu",
    },
    {
      name: "Nasty Plot",
      item: "Life Orb",
      ability: "Adaptability",
      nature: "Timid",
      evs: { spa: 252, spd: 4, spe: 252 },
      moves: ["Nasty Plot", "Aura Sphere", "Flash Cannon", "Vacuum Wave"],
      tier: "uu",
    },
  ],

  Mimikyu: [
    {
      name: "Swords Dance",
      item: "Life Orb",
      ability: "Disguise",
      nature: "Adamant",
      evs: { hp: 4, atk: 252, spe: 252 },
      moves: ["Swords Dance", "Shadow Sneak", "Play Rough", "Shadow Claw"],
      tier: "uu",
    },
  ],

  // ─── AG / UBERS ──────────────────────────────────────────────────────────────

  Mewtwo: [
    {
      name: "Calm Mind",
      item: "Life Orb",
      ability: "Pressure",
      nature: "Timid",
      evs: { spa: 252, spd: 4, spe: 252 },
      moves: ["Calm Mind", "Psystrike", "Ice Beam", "Aura Sphere"],
      tier: "ag",
    },
  ],

  "Calyrex-Shadow": [
    {
      name: "Nasty Plot",
      item: "Choice Scarf",
      ability: "As One (Spectrier)",
      nature: "Timid",
      evs: { spa: 252, spd: 4, spe: 252 },
      moves: ["Astral Barrage", "Draining Kiss", "Psyshock", "Trick"],
      tier: "ag",
    },
  ],

  Zacian: [
    {
      name: "Choice Band",
      item: "Choice Band",
      ability: "Intrepid Sword",
      nature: "Jolly",
      evs: { atk: 252, def: 4, spe: 252 },
      moves: ["Behemoth Blade", "Play Rough", "Close Combat", "Wild Charge"],
      tier: "ag",
    },
  ],

  Yveltal: [
    {
      name: "Specially Defensive",
      item: "Heavy-Duty Boots",
      ability: "Dark Aura",
      nature: "Careful",
      evs: { hp: 248, atk: 8, spd: 252 },
      moves: ["Knock Off", "Sucker Punch", "Roost", "Defog"],
      tier: "ag",
    },
  ],

  Kyogre: [
    {
      name: "Choice Scarf",
      item: "Choice Scarf",
      ability: "Drizzle",
      nature: "Timid",
      evs: { spa: 252, spd: 4, spe: 252 },
      moves: ["Water Spout", "Origin Pulse", "Ice Beam", "Thunder"],
      tier: "ag",
    },
  ],

  Groudon: [
    {
      name: "Stealth Rock",
      item: "Leftovers",
      ability: "Drought",
      nature: "Careful",
      evs: { hp: 252, atk: 4, spd: 252 },
      moves: ["Stealth Rock", "Precipice Blades", "Thunder Wave", "Rock Polish"],
      tier: "ag",
    },
  ],

  // ─── CLASSIC FAVOURITES ───────────────────────────────────────────────────────

  Tyranitar: [
    {
      name: "Choice Band",
      item: "Choice Band",
      ability: "Sand Stream",
      nature: "Adamant",
      evs: { hp: 4, atk: 252, spe: 252 },
      moves: ["Stone Edge", "Crunch", "Pursuit", "Earthquake"],
      tier: "ou",
    },
    {
      name: "Stealth Rock",
      item: "Smooth Rock",
      ability: "Sand Stream",
      nature: "Jolly",
      evs: { hp: 4, atk: 252, spe: 252 },
      moves: ["Stealth Rock", "Rock Blast", "Crunch", "Earthquake"],
      tier: "ou",
    },
  ],

  Bisharp: [
    {
      name: "Swords Dance",
      item: "Life Orb",
      ability: "Defiant",
      nature: "Adamant",
      evs: { hp: 4, atk: 252, spe: 252 },
      moves: ["Swords Dance", "Knock Off", "Iron Head", "Sucker Punch"],
      tier: "ou",
    },
  ],

  Blissey: [
    {
      name: "Specially Defensive",
      item: "Heavy-Duty Boots",
      ability: "Natural Cure",
      nature: "Calm",
      evs: { hp: 252, def: 252, spd: 4 },
      moves: ["Soft-Boiled", "Seismic Toss", "Ice Beam", "Thunder Wave"],
      tier: "ou",
    },
  ],

  Rotom: [
    {
      name: "Choice Scarf",
      item: "Choice Scarf",
      ability: "Levitate",
      nature: "Timid",
      evs: { spa: 252, spd: 4, spe: 252 },
      moves: ["Hydro Pump", "Volt Switch", "Trick", "Pain Split"],
      tier: "uu",
    },
  ],

  Gengar: [
    {
      name: "Nasty Plot",
      item: "Black Sludge",
      ability: "Cursed Body",
      nature: "Timid",
      evs: { spa: 252, spd: 4, spe: 252 },
      moves: ["Nasty Plot", "Shadow Ball", "Focus Blast", "Sludge Wave"],
      tier: "uu",
    },
  ],

  Gyarados: [
    {
      name: "Dragon Dance",
      item: "Lum Berry",
      ability: "Intimidate",
      nature: "Adamant",
      evs: { atk: 252, def: 4, spe: 252 },
      moves: ["Dragon Dance", "Waterfall", "Crunch", "Ice Fang"],
      tier: "ou",
    },
  ],

  Espeon: [
    {
      name: "Calm Mind",
      item: "Life Orb",
      ability: "Magic Bounce",
      nature: "Timid",
      evs: { spa: 252, spd: 4, spe: 252 },
      moves: ["Calm Mind", "Psyshock", "Dazzling Gleam", "Shadow Ball"],
      tier: "uu",
    },
  ],

  Jirachi: [
    {
      name: "Choice Scarf",
      item: "Choice Scarf",
      ability: "Serene Grace",
      nature: "Jolly",
      evs: { atk: 252, def: 4, spe: 252 },
      moves: ["Iron Head", "U-turn", "Healing Wish", "Fire Punch"],
      tier: "ou",
    },
  ],

  Breloom: [
    {
      name: "Spore Lead",
      item: "Focus Sash",
      ability: "Technician",
      nature: "Jolly",
      evs: { atk: 252, def: 4, spe: 252 },
      moves: ["Spore", "Bullet Seed", "Mach Punch", "Rock Tomb"],
      tier: "uu",
    },
  ],

  Medicham: [
    {
      name: "Choice Scarf",
      item: "Choice Scarf",
      ability: "Pure Power",
      nature: "Jolly",
      evs: { atk: 252, def: 4, spe: 252 },
      moves: ["High Jump Kick", "Zen Headbutt", "Ice Punch", "Trick"],
      tier: "uu",
    },
  ],

  // ─── STARTERS & FAN FAVORITES ──────────────────────────────────────────────

  Charizard: [{ name: "Special Attacker", item: "Heavy-Duty Boots", ability: "Solar Power", nature: "Timid", evs: { spa: 252, spd: 4, spe: 252 }, moves: ["Fire Blast", "Air Slash", "Focus Blast", "Roost"], tier: "ou" }],
  Blastoise: [{ name: "Rapid Spin", item: "Leftovers", ability: "Rain Dish", nature: "Bold", evs: { hp: 252, def: 252, spd: 4 }, moves: ["Scald", "Rapid Spin", "Ice Beam", "Toxic"], tier: "uu" }],
  Venusaur: [{ name: "Chlorophyll Sweeper", item: "Life Orb", ability: "Chlorophyll", nature: "Modest", evs: { spa: 252, spd: 4, spe: 252 }, moves: ["Giga Drain", "Sludge Bomb", "Weather Ball", "Growth"], tier: "ou" }],
  Pikachu: [{ name: "Light Ball", item: "Light Ball", ability: "Lightning Rod", nature: "Timid", evs: { spa: 252, spd: 4, spe: 252 }, moves: ["Thunderbolt", "Volt Switch", "Surf", "Nasty Plot"], tier: "uu" }],
  Alakazam: [{ name: "Special Sweeper", item: "Focus Sash", ability: "Magic Guard", nature: "Timid", evs: { spa: 252, spd: 4, spe: 252 }, moves: ["Psychic", "Shadow Ball", "Focus Blast", "Nasty Plot"], tier: "ou" }],
  Machamp: [{ name: "Guts", item: "Flame Orb", ability: "Guts", nature: "Adamant", evs: { hp: 252, atk: 252, spe: 4 }, moves: ["Close Combat", "Knock Off", "Facade", "Bullet Punch"], tier: "uu" }],
  Snorlax: [{ name: "Curse", item: "Leftovers", ability: "Thick Fat", nature: "Careful", evs: { hp: 252, def: 4, spd: 252 }, moves: ["Body Slam", "Earthquake", "Curse", "Rest"], tier: "uu" }],
  Lapras: [{ name: "Offensive", item: "Choice Specs", ability: "Water Absorb", nature: "Modest", evs: { hp: 4, spa: 252, spe: 252 }, moves: ["Hydro Pump", "Ice Beam", "Thunderbolt", "Freeze-Dry"], tier: "uu" }],
  Arcanine: [{ name: "Bulky Attacker", item: "Heavy-Duty Boots", ability: "Intimidate", nature: "Jolly", evs: { atk: 252, def: 4, spe: 252 }, moves: ["Flare Blitz", "Wild Charge", "Extreme Speed", "Morning Sun"], tier: "uu" }],
  Starmie: [{ name: "Special Sweeper", item: "Life Orb", ability: "Analytic", nature: "Timid", evs: { spa: 252, spd: 4, spe: 252 }, moves: ["Hydro Pump", "Thunderbolt", "Ice Beam", "Rapid Spin"], tier: "uu" }],
  Aerodactyl: [{ name: "Lead", item: "Focus Sash", ability: "Unnerve", nature: "Jolly", evs: { atk: 252, spd: 4, spe: 252 }, moves: ["Stone Edge", "Earthquake", "Stealth Rock", "Taunt"], tier: "uu" }],
  Metagross: [{ name: "Assault Vest", item: "Assault Vest", ability: "Clear Body", nature: "Adamant", evs: { hp: 252, atk: 252, spd: 4 }, moves: ["Meteor Mash", "Earthquake", "Ice Punch", "Bullet Punch"], tier: "ou" }],
  Swampert: [{ name: "Stealth Rock", item: "Leftovers", ability: "Torrent", nature: "Relaxed", evs: { hp: 252, def: 252, atk: 4 }, moves: ["Stealth Rock", "Earthquake", "Scald", "Toxic"], tier: "ou" }],
  Blaziken: [{ name: "Speed Boost Sweeper", item: "Life Orb", ability: "Speed Boost", nature: "Adamant", evs: { atk: 252, spd: 4, spe: 252 }, moves: ["Flare Blitz", "Close Combat", "Swords Dance", "Protect"], tier: "ou" }],
  Sceptile: [{ name: "Special Attacker", item: "Life Orb", ability: "Overgrow", nature: "Timid", evs: { spa: 252, spd: 4, spe: 252 }, moves: ["Leaf Storm", "Focus Blast", "Dragon Pulse", "Giga Drain"], tier: "uu" }],
  Milotic: [{ name: "Defensive", item: "Leftovers", ability: "Marvel Scale", nature: "Bold", evs: { hp: 252, def: 252, spe: 4 }, moves: ["Scald", "Ice Beam", "Recover", "Haze"], tier: "uu" }],
  Infernape: [{ name: "Mixed Attacker", item: "Life Orb", ability: "Iron Fist", nature: "Naive", evs: { atk: 252, spa: 4, spe: 252 }, moves: ["Close Combat", "Flare Blitz", "U-turn", "Mach Punch"], tier: "uu" }],
  Empoleon: [{ name: "Stealth Rock", item: "Leftovers", ability: "Torrent", nature: "Modest", evs: { hp: 252, spa: 252, spd: 4 }, moves: ["Scald", "Flash Cannon", "Stealth Rock", "Defog"], tier: "uu" }],
  Togekiss: [{ name: "Nasty Plot", item: "Leftovers", ability: "Serene Grace", nature: "Timid", evs: { hp: 4, spa: 252, spe: 252 }, moves: ["Air Slash", "Dazzling Gleam", "Nasty Plot", "Roost"], tier: "ou" }],
  Excadrill: [{ name: "Sand Rush", item: "Life Orb", ability: "Sand Rush", nature: "Jolly", evs: { atk: 252, def: 4, spe: 252 }, moves: ["Earthquake", "Iron Head", "Rock Slide", "Swords Dance"], tier: "ou" }],
  Zoroark: [{ name: "Nasty Plot", item: "Life Orb", ability: "Illusion", nature: "Timid", evs: { spa: 252, spd: 4, spe: 252 }, moves: ["Dark Pulse", "Flamethrower", "Focus Blast", "Nasty Plot"], tier: "uu" }],
  Greninja: [{ name: "Special Attacker", item: "Choice Specs", ability: "Protean", nature: "Timid", evs: { spa: 252, spd: 4, spe: 252 }, moves: ["Hydro Pump", "Dark Pulse", "Ice Beam", "U-turn"], tier: "ou" }],
  Aegislash: [{ name: "Special", item: "Leftovers", ability: "Stance Change", nature: "Modest", evs: { hp: 252, spa: 252, spd: 4 }, moves: ["Shadow Ball", "Flash Cannon", "King's Shield", "Shadow Sneak"], tier: "ou" }],
  Talonflame: [{ name: "Swords Dance", item: "Heavy-Duty Boots", ability: "Gale Wings", nature: "Jolly", evs: { atk: 252, def: 4, spe: 252 }, moves: ["Brave Bird", "Flare Blitz", "Swords Dance", "Roost"], tier: "uu" }],
  Cinderace: [{ name: "Court Change", item: "Heavy-Duty Boots", ability: "Libero", nature: "Jolly", evs: { atk: 252, spd: 4, spe: 252 }, moves: ["Pyro Ball", "High Jump Kick", "U-turn", "Court Change"], tier: "ou" }],
  Rillaboom: [{ name: "Choice Band", item: "Choice Band", ability: "Grassy Surge", nature: "Adamant", evs: { hp: 4, atk: 252, spe: 252 }, moves: ["Grassy Glide", "Wood Hammer", "Knock Off", "U-turn"], tier: "ou" }],
  Inteleon: [{ name: "Sniper", item: "Scope Lens", ability: "Sniper", nature: "Timid", evs: { spa: 252, spd: 4, spe: 252 }, moves: ["Snipe Shot", "Ice Beam", "Shadow Ball", "U-turn"], tier: "uu" }],
  Magnezone: [{ name: "Trapper", item: "Choice Specs", ability: "Magnet Pull", nature: "Modest", evs: { hp: 4, spa: 252, spe: 252 }, moves: ["Thunderbolt", "Flash Cannon", "Volt Switch", "Body Press"], tier: "ou" }],
  Azumarill: [{ name: "Belly Drum", item: "Sitrus Berry", ability: "Huge Power", nature: "Adamant", evs: { hp: 252, atk: 252, spe: 4 }, moves: ["Aqua Jet", "Play Rough", "Belly Drum", "Knock Off"], tier: "ou" }],
  Mamoswine: [{ name: "Lead", item: "Focus Sash", ability: "Oblivious", nature: "Jolly", evs: { atk: 252, spd: 4, spe: 252 }, moves: ["Earthquake", "Ice Shard", "Icicle Crash", "Stealth Rock"], tier: "ou" }],
  Reuniclus: [{ name: "Calm Mind", item: "Life Orb", ability: "Magic Guard", nature: "Bold", evs: { hp: 252, def: 252, spa: 4 }, moves: ["Psychic", "Focus Blast", "Calm Mind", "Recover"], tier: "uu" }],
  Conkeldurr: [{ name: "Guts", item: "Flame Orb", ability: "Guts", nature: "Adamant", evs: { hp: 252, atk: 252, def: 4 }, moves: ["Close Combat", "Knock Off", "Mach Punch", "Facade"], tier: "uu" }],
  Chansey: [{ name: "Eviolite Wall", item: "Eviolite", ability: "Natural Cure", nature: "Bold", evs: { hp: 252, def: 252, spd: 4 }, moves: ["Seismic Toss", "Soft-Boiled", "Stealth Rock", "Thunder Wave"], tier: "ou" }],
  Skarmory: [{ name: "Physically Defensive", item: "Rocky Helmet", ability: "Sturdy", nature: "Impish", evs: { hp: 252, def: 252, spd: 4 }, moves: ["Body Press", "Roost", "Spikes", "Whirlwind"], tier: "ou" }],
  Hippowdon: [{ name: "Sand Stream Wall", item: "Leftovers", ability: "Sand Stream", nature: "Impish", evs: { hp: 252, def: 252, spd: 4 }, moves: ["Earthquake", "Stealth Rock", "Slack Off", "Whirlwind"], tier: "ou" }],
  Amoonguss: [{ name: "Regenerator", item: "Black Sludge", ability: "Regenerator", nature: "Bold", evs: { hp: 252, def: 252, spd: 4 }, moves: ["Spore", "Giga Drain", "Sludge Bomb", "Clear Smog"], tier: "ou" }],
  Ditto: [{ name: "Imposter", item: "Choice Scarf", ability: "Imposter", nature: "Relaxed", evs: { hp: 252, def: 252, spd: 4 }, moves: ["Transform", "Transform", "Transform", "Transform"], tier: "ou" }],
  Pelipper: [{ name: "Drizzle Support", item: "Damp Rock", ability: "Drizzle", nature: "Bold", evs: { hp: 252, def: 252, spd: 4 }, moves: ["Scald", "Hurricane", "U-turn", "Roost"], tier: "ou" }],
  Ninetales: [{ name: "Drought Support", item: "Heat Rock", ability: "Drought", nature: "Timid", evs: { spa: 252, spd: 4, spe: 252 }, moves: ["Flamethrower", "Solar Beam", "Nasty Plot", "Will-O-Wisp"], tier: "uu" }],
  Slowbro: [{ name: "Physically Defensive", item: "Heavy-Duty Boots", ability: "Regenerator", nature: "Bold", evs: { hp: 252, def: 252, spa: 4 }, moves: ["Scald", "Psychic", "Slack Off", "Thunder Wave"], tier: "ou" }],
  Tentacruel: [{ name: "Toxic Spikes", item: "Black Sludge", ability: "Liquid Ooze", nature: "Timid", evs: { hp: 252, def: 4, spe: 252 }, moves: ["Scald", "Sludge Bomb", "Toxic Spikes", "Rapid Spin"], tier: "uu" }],
  Weezing: [{ name: "Physically Defensive", item: "Black Sludge", ability: "Levitate", nature: "Bold", evs: { hp: 252, def: 252, spd: 4 }, moves: ["Sludge Bomb", "Flamethrower", "Will-O-Wisp", "Pain Split"], tier: "uu" }],
  Quagsire: [{ name: "Unaware Wall", item: "Leftovers", ability: "Unaware", nature: "Relaxed", evs: { hp: 252, def: 252, spd: 4 }, moves: ["Earthquake", "Scald", "Recover", "Toxic"], tier: "uu" }],
  Dondozo: [{ name: "Unaware Wall", item: "Leftovers", ability: "Unaware", nature: "Impish", evs: { hp: 252, def: 252, spd: 4 }, moves: ["Waterfall", "Earthquake", "Rest", "Sleep Talk"], tier: "ou" }],
  Annihilape: [{ name: "Bulk Up", item: "Leftovers", ability: "Defiant", nature: "Careful", evs: { hp: 252, spd: 252, def: 4 }, moves: ["Rage Fist", "Drain Punch", "Bulk Up", "Rest"], tier: "ou" }],
  Ceruledge: [{ name: "Swords Dance", item: "Heavy-Duty Boots", ability: "Flash Fire", nature: "Jolly", evs: { atk: 252, spd: 4, spe: 252 }, moves: ["Bitter Blade", "Shadow Sneak", "Swords Dance", "Close Combat"], tier: "ou" }],
  Tinkaton: [{ name: "Stealth Rock", item: "Leftovers", ability: "Mold Breaker", nature: "Impish", evs: { hp: 252, def: 252, atk: 4 }, moves: ["Gigaton Hammer", "Knock Off", "Stealth Rock", "Thunder Wave"], tier: "ou" }],
  Gardevoir: [{ name: "Calm Mind", item: "Leftovers", ability: "Trace", nature: "Timid", evs: { hp: 4, spa: 252, spe: 252 }, moves: ["Moonblast", "Psychic", "Calm Mind", "Mystical Fire"], tier: "uu" }],
  Gallade: [{ name: "Swords Dance", item: "Life Orb", ability: "Sharpness", nature: "Jolly", evs: { atk: 252, spd: 4, spe: 252 }, moves: ["Close Combat", "Psycho Cut", "Shadow Sneak", "Swords Dance"], tier: "uu" }],
  Umbreon: [{ name: "Wish Passer", item: "Leftovers", ability: "Synchronize", nature: "Calm", evs: { hp: 252, def: 4, spd: 252 }, moves: ["Foul Play", "Wish", "Protect", "Heal Bell"], tier: "uu" }],
  Vaporeon: [{ name: "Wish Passer", item: "Leftovers", ability: "Water Absorb", nature: "Bold", evs: { hp: 252, def: 252, spa: 4 }, moves: ["Scald", "Wish", "Protect", "Ice Beam"], tier: "uu" }],
  Jolteon: [{ name: "Special Attacker", item: "Choice Specs", ability: "Volt Absorb", nature: "Timid", evs: { spa: 252, spd: 4, spe: 252 }, moves: ["Thunderbolt", "Volt Switch", "Shadow Ball", "Signal Beam"], tier: "uu" }],
  Flareon: [{ name: "Guts", item: "Flame Orb", ability: "Guts", nature: "Adamant", evs: { hp: 252, atk: 252, def: 4 }, moves: ["Flare Blitz", "Facade", "Superpower", "Quick Attack"], tier: "uu" }],
  Sylveon: [{ name: "Calm Mind", item: "Leftovers", ability: "Pixilate", nature: "Modest", evs: { hp: 252, spa: 252, spe: 4 }, moves: ["Hyper Voice", "Mystical Fire", "Calm Mind", "Wish"], tier: "uu" }],
  Leafeon: [{ name: "Swords Dance", item: "Life Orb", ability: "Chlorophyll", nature: "Jolly", evs: { atk: 252, def: 4, spe: 252 }, moves: ["Leaf Blade", "Knock Off", "X-Scissor", "Swords Dance"], tier: "uu" }],
  Glaceon: [{ name: "Choice Specs", item: "Choice Specs", ability: "Snow Cloak", nature: "Modest", evs: { hp: 4, spa: 252, spe: 252 }, moves: ["Ice Beam", "Shadow Ball", "Water Pulse", "Freeze-Dry"], tier: "uu" }],
  Tauros: [{ name: "Choice Scarf", item: "Choice Scarf", ability: "Sheer Force", nature: "Jolly", evs: { atk: 252, def: 4, spe: 252 }, moves: ["Close Combat", "Flare Blitz", "Rock Slide", "Zen Headbutt"], tier: "uu" }],
  Heracross: [{ name: "Choice Scarf", item: "Choice Scarf", ability: "Moxie", nature: "Jolly", evs: { atk: 252, def: 4, spe: 252 }, moves: ["Close Combat", "Megahorn", "Knock Off", "Earthquake"], tier: "uu" }],
  Absol: [{ name: "Swords Dance", item: "Life Orb", ability: "Super Luck", nature: "Jolly", evs: { atk: 252, spd: 4, spe: 252 }, moves: ["Knock Off", "Sucker Punch", "Swords Dance", "Close Combat"], tier: "uu" }],
  Luxray: [{ name: "Guts", item: "Flame Orb", ability: "Guts", nature: "Adamant", evs: { atk: 252, def: 4, spe: 252 }, moves: ["Wild Charge", "Facade", "Crunch", "Ice Fang"], tier: "uu" }],
  Staraptor: [{ name: "Choice Band", item: "Choice Band", ability: "Reckless", nature: "Jolly", evs: { atk: 252, def: 4, spe: 252 }, moves: ["Brave Bird", "Double-Edge", "Close Combat", "U-turn"], tier: "uu" }],
  Toxicroak: [{ name: "Swords Dance", item: "Life Orb", ability: "Dry Skin", nature: "Jolly", evs: { atk: 252, spd: 4, spe: 252 }, moves: ["Drain Punch", "Gunk Shot", "Sucker Punch", "Swords Dance"], tier: "uu" }],
  Electivire: [{ name: "Mixed Attacker", item: "Life Orb", ability: "Motor Drive", nature: "Naive", evs: { atk: 252, spa: 4, spe: 252 }, moves: ["Wild Charge", "Ice Punch", "Earthquake", "Flamethrower"], tier: "uu" }],
  Magmortar: [{ name: "Special Attacker", item: "Choice Specs", ability: "Vital Spirit", nature: "Modest", evs: { spa: 252, spd: 4, spe: 252 }, moves: ["Fire Blast", "Thunderbolt", "Focus Blast", "Psychic"], tier: "uu" }],
  Flygon: [{ name: "Choice Scarf", item: "Choice Scarf", ability: "Levitate", nature: "Jolly", evs: { atk: 252, def: 4, spe: 252 }, moves: ["Earthquake", "Outrage", "U-turn", "Stone Edge"], tier: "uu" }],
  Aggron: [{ name: "Stealth Rock", item: "Leftovers", ability: "Sturdy", nature: "Impish", evs: { hp: 252, def: 252, atk: 4 }, moves: ["Iron Head", "Earthquake", "Stealth Rock", "Roar"], tier: "uu" }],
  Crawdaunt: [{ name: "Swords Dance", item: "Life Orb", ability: "Adaptability", nature: "Adamant", evs: { atk: 252, spd: 4, spe: 252 }, moves: ["Crabhammer", "Knock Off", "Aqua Jet", "Swords Dance"], tier: "uu" }],
  Hawlucha: [{ name: "Unburden Sweeper", item: "Electric Seed", ability: "Unburden", nature: "Adamant", evs: { atk: 252, spd: 4, spe: 252 }, moves: ["Close Combat", "Acrobatics", "Swords Dance", "Roost"], tier: "ou" }],
  Mienshao: [{ name: "Choice Scarf", item: "Choice Scarf", ability: "Reckless", nature: "Jolly", evs: { atk: 252, spd: 4, spe: 252 }, moves: ["High Jump Kick", "U-turn", "Knock Off", "Stone Edge"], tier: "uu" }],
  Darmanitan: [{ name: "Choice Scarf", item: "Choice Scarf", ability: "Sheer Force", nature: "Jolly", evs: { atk: 252, def: 4, spe: 252 }, moves: ["Flare Blitz", "Earthquake", "U-turn", "Rock Slide"], tier: "uu" }],
  Haxorus: [{ name: "Dragon Dance", item: "Lum Berry", ability: "Mold Breaker", nature: "Jolly", evs: { atk: 252, def: 4, spe: 252 }, moves: ["Outrage", "Earthquake", "Poison Jab", "Dragon Dance"], tier: "uu" }],
  Pangoro: [{ name: "Choice Band", item: "Choice Band", ability: "Iron Fist", nature: "Adamant", evs: { hp: 4, atk: 252, spe: 252 }, moves: ["Close Combat", "Knock Off", "Ice Punch", "Parting Shot"], tier: "uu" }],
  Noivern: [{ name: "Pivot", item: "Choice Specs", ability: "Infiltrator", nature: "Timid", evs: { spa: 252, spd: 4, spe: 252 }, moves: ["Draco Meteor", "Hurricane", "Flamethrower", "U-turn"], tier: "uu" }],
  Araquanid: [{ name: "Sticky Web", item: "Focus Sash", ability: "Water Bubble", nature: "Adamant", evs: { hp: 252, atk: 252, def: 4 }, moves: ["Liquidation", "Sticky Web", "Mirror Coat", "Leech Life"], tier: "uu" }],
  Tsareena: [{ name: "Rapid Spin", item: "Heavy-Duty Boots", ability: "Queenly Majesty", nature: "Jolly", evs: { atk: 252, spd: 4, spe: 252 }, moves: ["Power Whip", "High Jump Kick", "Rapid Spin", "U-turn"], tier: "uu" }],
  Kommo_o: [{ name: "Clangorous Soul", item: "Throat Spray", ability: "Bulletproof", nature: "Timid", evs: { spa: 252, spd: 4, spe: 252 }, moves: ["Clangorous Soulblaze", "Clanging Scales", "Flamethrower", "Aura Sphere"], tier: "ou" }],
  Copperajah: [{ name: "Assault Vest", item: "Assault Vest", ability: "Sheer Force", nature: "Adamant", evs: { hp: 252, atk: 252, spd: 4 }, moves: ["Iron Head", "Earthquake", "Ice Punch", "Rock Slide"], tier: "uu" }],
  Dragalge: [{ name: "Special Attacker", item: "Choice Specs", ability: "Adaptability", nature: "Modest", evs: { hp: 252, spa: 252, spe: 4 }, moves: ["Draco Meteor", "Sludge Wave", "Focus Blast", "Thunderbolt"], tier: "uu" }],
  Goodra: [{ name: "Assault Vest", item: "Assault Vest", ability: "Sap Sipper", nature: "Modest", evs: { hp: 252, spa: 252, spd: 4 }, moves: ["Draco Meteor", "Sludge Bomb", "Flamethrower", "Thunderbolt"], tier: "uu" }],
  Baxcalibur: [{ name: "Dragon Dance", item: "Loaded Dice", ability: "Thermal Exchange", nature: "Jolly", evs: { atk: 252, def: 4, spe: 252 }, moves: ["Icicle Spear", "Dragon Claw", "Earthquake", "Dragon Dance"], tier: "ou" }],
  Clodsire: [{ name: "Unaware Wall", item: "Leftovers", ability: "Unaware", nature: "Careful", evs: { hp: 252, def: 4, spd: 252 }, moves: ["Earthquake", "Toxic", "Recover", "Stealth Rock"], tier: "ou" }],
  Grafaiai: [{ name: "Toxic Chain", item: "Black Sludge", ability: "Prankster", nature: "Jolly", evs: { atk: 252, spd: 4, spe: 252 }, moves: ["Knock Off", "Gunk Shot", "U-turn", "Parting Shot"], tier: "uu" }],
  Palossand: [{ name: "Stealth Rock", item: "Colbur Berry", ability: "Water Compaction", nature: "Bold", evs: { hp: 252, def: 252, spa: 4 }, moves: ["Earth Power", "Shadow Ball", "Stealth Rock", "Shore Up"], tier: "uu" }],
};
